import type { WidenMediaElement } from '@libs/widen-utils';
import { MediaElementType, WidenUtil } from '@libs/widen-utils';
import type { Teasers } from '@generated/graphql';
import type { AssetSource } from '@libs/assets.helper';
import { AssetSourceType, getSourceItemByDefaultLanguageAndType } from '@libs/assets.helper';
import type {
  AssociationProductFragmentEnhanced,
  JsonLdProductFragmentEnhanced,
  ModelAssociationProductFragmentEnhanced,
  OverviewProductAssetFragmentEnhanced,
  ProductAssetsFragmentEnhanced,
  ProductAssetsInformation,
  ProductAssetSource,
  ProductFragmentEnhanced,
} from '@models/enhanced-fragments';
import type { LabeledAttribute, ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import type { UnitCode } from '@icommerce/units';
import type { PriceQuantityUnit } from '@libs/product-price-utils';

export const ASSETS_ATTRIBUTES_PATTERN = [
  'attr_image',
  'attr_video',
  'drawing',
  'installation_dimension_drawing_02',
  'FLG_FLG_chart_01',
];

export enum GraphQlPriceType {
  Length = 'Length',
  Piece = 'Piece',
  LengthAndPiece = 'LengthAndPiece',
  LengthAndPieceWithoutCopper = 'LengthAndPieceWithoutCopper',
  LengthWithCuttingCostWithoutCopper = 'LengthWithCuttingCostWithoutCopper',
  PieceWithCopper = 'PieceWithCopper',
  LengthWithoutCopper = 'LengthWithoutCopper',
  ChainLink = 'ChainLink',
  PieceWithChainLengthDependency = 'PieceWithChainLengthDependency',
  ChainLength = 'ChainLength',
  LeadScrewLength = 'LeadScrewLength',
  TribotapeLength = 'TribotapeLength',
}

export interface GraphQlPrice {
  productId: string;
  priceType?: GraphQlPriceType;
  regularPrice?: number;
  discountPrice?: number;
  discountTierQuantity?: number;
  currency: string;
  countryCode: string;
}

/**
 * interface for certificate icons
 */
export interface CertificateIcon {
  key: string;
  url: string;
  label: string;
}

/**
 * interface for copper prices
 */
export interface CopperPrice {
  price?: number;
  currency_code: string;
  unit?: string;
  unit_multiplier?: number;
}

/**
 * interface for Measurement
 */
export interface Measurement<T> {
  value: T;
  unit?: UnitCode;
}

/**
 * interface for translation option
 */
export interface TranslationOption {
  locale: string;
}

/**
 * interface for contact person
 */
export interface ContactPerson {
  businessUnit?: string;
  name: string;
  phone?: string;
  photo?: string;
  messageParams?: string[];
}

interface TeaserContentSnippet {
  title?: string;
  text?: string;
  url?: string;
}

interface TeaserContentImage {
  extId: string;
  url: string;
}

export interface TeaserContent {
  id: number;
  default: TeaserContentSnippet;
  local?: TeaserContentSnippet;
  imageDam?: TeaserContentImage;
  image9_5?: string;
  image1_1?: string;
  image75_181?: string;
}

export interface TeaserContentFail {
  errorCode: number;
  errorMessage: string;
  errorData: string;
}

export type ProductWithConfiguredRedirect = Array<{ modelId: string; domain: string; targetPath: string }>;

export const getAssetSource = (asset: ProductAssetsFragmentEnhanced, locale?: string): ProductAssetSource => {
  if (locale) {
    // source.key is expected to contain a locale value (e.g., "de-DE", "fr-FR")
    return asset.sources.find(source => source.key === locale) ?? asset.sources[0];
  }
  return asset.sources[0];
};

/**
 * interface to get available functions by ProductAssetsHandler
 */
export interface ProductAssetsFunctions {
  getDownloadUrl(locale: string): string | null;

  getPreviewUrl(locale: string): string | null;

  getDownloadContentType(locale: string): string | null;

  getDescription(): string;

  getKey(): string;
}

export const ProductAssetsHandler = (asset: ProductAssetsFragmentEnhanced): ProductAssetsFunctions =>
  <ProductAssetsFunctions>{
    getKey(): string {
      return asset.key;
    },
    getDescription(): string {
      return asset.description ?? '';
    },
    getDownloadUrl(locale): string | null {
      const item = getSourceItemByDefaultLanguageAndType(asset, AssetSourceType.Download, locale);
      if (!item) {
        return null;
      }
      return item.uri;
    },
    getPreviewUrl(locale): string | null {
      const item = getSourceItemByDefaultLanguageAndType(asset, AssetSourceType.Preview, locale);
      if (!item) {
        return null;
      }
      return item.uri;
    },
    getDownloadContentType(locale): string | null {
      const item = getSourceItemByDefaultLanguageAndType(asset, AssetSourceType.Download, locale);
      if (!item) {
        return null;
      }
      return item.contentType || null;
    },
  };

export type ProductTeaserPrices = {
  currency: string;
  unit?: PriceQuantityUnit | string;
  priceType?: GraphQlPriceType;
  lowestRegularPrice: number; // lowest regular price of a variant without any discount
  discountedPrice: number; // discounted price that will be shown (for the variant with the lowest price)
  discountCategory: number; // category of the discounted price, for example 100
  minLengthRegularPrice?: number;
};

export type DeliveryTimeAggregate = {
  key: string | null;
  text: string | null;
  variantCount: number;
};

export const getTeaserImageURL = (productAssetsInfo: ProductAssetsInformation): string => {
  if (!productAssetsInfo) {
    return null;
  }
  const teaserImageAssets = productAssetsInfo?.assets?.filter(item => item.key?.includes('product_teaser_image'));
  const teaserImageSource: AssetSource = teaserImageAssets[0]?.sources[0] as AssetSource;
  return teaserImageSource?.uri ? `https:${teaserImageSource?.uri}` : null;
};

export abstract class ProductHelper {
  static getAssetsMedia(
    product: Readonly<
      | ProductFragmentEnhanced
      | AssociationProductFragmentEnhanced
      | ModelAssociationProductFragmentEnhanced
      | JsonLdProductFragmentEnhanced
    >,
    key?: string,
    locale?: string
  ): {
    productMedia: WidenMediaElement[];
    technicalMedia: WidenMediaElement[];
  } {
    const productMedia: WidenMediaElement[] = [];
    const technicalMedia: WidenMediaElement[] = [];

    product.assets
      .filter(asset => (key ? asset.key === key : asset))
      .filter(asset => 'sources' in asset && asset.sources && asset.sources.length > 0)
      .forEach(asset => {
        if (ASSETS_ATTRIBUTES_PATTERN.find(pattern => asset.key?.includes(pattern))) {
          let mediaElement: WidenMediaElement;

          if (asset.key?.includes('attr_video')) {
            const assetSource = getAssetSource(asset, locale);
            mediaElement = WidenUtil.extractWidenVideo(assetSource?.uri);
            mediaElement.title = `${product.id} video`;
            mediaElement.widenId = assetSource?.widenId;
          } else if (asset.key?.includes('attr_image')) {
            const assetSource = getAssetSource(asset, locale);
            mediaElement = {
              type: MediaElementType.IMAGE,
              title: `${product.id} product image`,
              shortCode: WidenUtil.extractWidenImageId(assetSource?.uri),
              filename: WidenUtil.extractWidenImageName(assetSource?.uri),
              technicalAttributes: asset.metadata?.technicalAttributes,
              widenId: assetSource?.widenId,
            };
          } else {
            const assetSource = getAssetSource(asset, locale);
            mediaElement = {
              type: MediaElementType.DRAWING,
              title: `${product.id} technical drawing`,
              shortCode: WidenUtil.extractWidenImageId(assetSource.uri),
              filename: WidenUtil.extractWidenImageName(assetSource.uri),
              technicalAttributes: asset.metadata?.technicalAttributes,
              widenId: assetSource?.widenId,
            };
          }

          mediaElement.heading = (product?.attributes?.series_description as LabeledAttribute<string>)?.value;

          if (asset.description && asset.description !== 'default') {
            mediaElement.description = asset.description;
          }

          productMedia.push(mediaElement);
        }
        if (asset.key?.includes('diagram')) {
          const diagramNr = asset.key.replace(/^\D+|\D+$/g, '');
          const diagramUnderlineKey = `diagram_asset_underline_${diagramNr}`;
          type ObjectKey = keyof typeof product;
          let underline: string;
          if ('attributes' in product) {
            underline =
              getAttributeValue<string>(product.attributes?.[diagramUnderlineKey] as ProductAttribute<string>) ?? '';
          } else {
            underline =
              getAttributeValue<string>(product[diagramUnderlineKey as ObjectKey] as ProductAttribute<string>) ?? '';
          }
          underline = underline.replace('\n', '<br/>');

          const mediaElement: WidenMediaElement = {
            widenId: asset.sources[0].widenId,
            sort: parseInt(diagramNr, 10),
            type: MediaElementType.DIAGRAM,
            shortCode: WidenUtil.extractWidenImageId(asset.sources[0].uri),
            filename: WidenUtil.extractWidenImageName(asset.sources[0].uri),
            underline, // can be ignored for JsonLdProductFragmentEnhanced
            title: `${product.id} diagram`,
          };

          mediaElement.heading = product?.attributes?.series_description as string;

          if (asset.description && asset.description !== 'default') {
            mediaElement.description = asset.description;
          }

          technicalMedia.push(mediaElement);
        }
      });

    return {
      productMedia,
      technicalMedia,
    };
  }

  static removeTrailingZeros(stringValue: string): string {
    if (stringValue === '' || Number.isNaN(Number(stringValue))) {
      return stringValue;
    }

    // parse it to number and then reformat it back to string, to get rid of trailing zeros
    return String(parseFloat(stringValue));
  }

  static isDiscontinued(
    product: Readonly<ProductFragmentEnhanced | AssociationProductFragmentEnhanced | JsonLdProductFragmentEnhanced>
  ): boolean {
    return Boolean(
      getAttributeValue<boolean>(product.attributes?.attr_status_phase_out_model_boolean as ProductAttribute<boolean>)
    );
  }

  static isNew(product: Readonly<ProductFragmentEnhanced | AssociationProductFragmentEnhanced>): boolean {
    return Boolean(
      getAttributeValue<boolean>(product.attributes?.attr_status_new_product_boolean as ProductAttribute<boolean>)
    );
  }

  static isStandard(product: Readonly<ProductFragmentEnhanced | AssociationProductFragmentEnhanced>): boolean {
    return Boolean(getAttributeValue<boolean>(product.attributes?.standard_bool as ProductAttribute<boolean>));
  }

  static getTeaserImage(
    product: Readonly<ProductFragmentEnhanced | AssociationProductFragmentEnhanced>
  ): WidenMediaElement | null {
    return ProductHelper.getTeaserImageFromRawData(
      product.attributes?.series_description as ProductAttribute<string>,
      product.assets
    );
  }

  static getTeaserImageFromRawData(
    seriesDescriptionAttribute: ProductAttribute<string>,
    assets: OverviewProductAssetFragmentEnhanced[]
  ): WidenMediaElement | null {
    if (!assets || !assets.length) {
      return null;
    }

    let teaserElementTitle = '';
    if (seriesDescriptionAttribute) {
      teaserElementTitle = getAttributeValue<string>(seriesDescriptionAttribute).toString();
    }
    const teaserImageAssets = assets?.filter(item => item.key?.includes('product_teaser_image'));

    // TODO: remove ternary else case when we don't need the fallback for teaser image of cf_case anymore
    const teaserImages = (
      teaserImageAssets.length > 0 ? teaserImageAssets : assets.filter(item => item.key?.includes('image'))
    )?.map(asset => ProductHelper.convertImageUriToWidenImage(asset.sources[0]?.uri, teaserElementTitle));

    return teaserImages && teaserImages.length ? teaserImages[0] : null;
  }

  static convertImageUriToWidenImage(uri: string, title: string): WidenMediaElement {
    return {
      type: MediaElementType.IMAGE,
      shortCode: WidenUtil.extractWidenImageId(uri),
      filename: WidenUtil.extractWidenImageName(uri),
      title,
    };
  }

  static hasNewVariant(product: Readonly<ProductFragmentEnhanced | AssociationProductFragmentEnhanced>): boolean {
    return (
      (getAttributeValue<string>(product.attributes?.new_variant as ProductAttribute<string>)?.trim().length ?? 0) > 0
    );
  }

  static getOnlineTools(
    product: Readonly<ProductFragmentEnhanced | AssociationProductFragmentEnhanced>,
    onlineToolsDefinitions: Teasers[]
  ): Teasers[] {
    const onlineTools = getAttributeValue<string[]>(product.attributes?.online_tools) || [];
    return onlineToolsDefinitions.filter(teaser => onlineTools.find(online_tool => online_tool === teaser.id));
  }
}
